export type GoogleAddressComponent = {
  longName: string;
  shortName: string;
  types: string[];
};

export type AddressParts = {
  street: string;
  locality: string;
  region: string;
  postalCode: string;
  country: string;
  countryCode: string;
};

export type AddressSelection = AddressParts & {
  formattedAddress: string;
  googlePlaceId: string | null;
  latitude: number | null;
  longitude: number | null;
};

const EMPTY_ADDRESS_PARTS: AddressParts = {
  street: '',
  locality: '',
  region: '',
  postalCode: '',
  country: '',
  countryCode: '',
};

function findComponent(components: GoogleAddressComponent[], type: string) {
  return components.find((component) => component.types.includes(type));
}

/** Converts Google Places address components into editable, provider-neutral fields. */
export function parseGoogleAddressComponents(components: GoogleAddressComponent[]): AddressParts {
  const subpremise = findComponent(components, 'subpremise')?.longName ?? '';
  const streetNumber = findComponent(components, 'street_number')?.longName ?? '';
  const route = findComponent(components, 'route')?.longName ?? '';
  const streetBase = [streetNumber, route].filter(Boolean).join(' ');
  const street =
    subpremise && streetBase ? `${subpremise}/${streetBase}` : subpremise || streetBase;
  const locality =
    findComponent(components, 'locality')?.longName ??
    findComponent(components, 'postal_town')?.longName ??
    findComponent(components, 'sublocality')?.longName ??
    '';
  const region = findComponent(components, 'administrative_area_level_1')?.shortName ?? '';
  const postalCode = findComponent(components, 'postal_code')?.longName ?? '';
  const country = findComponent(components, 'country')?.longName ?? '';
  const countryCode = findComponent(components, 'country')?.shortName ?? '';

  return { street, locality, region, postalCode, country, countryCode };
}

export function formatAddress(parts: Partial<AddressParts>) {
  const localityLine = [parts.locality, parts.region, parts.postalCode].filter(Boolean).join(' ');
  return [parts.street, localityLine, parts.country].filter(Boolean).join(', ');
}

export function emptyAddressParts(): AddressParts {
  return { ...EMPTY_ADDRESS_PARTS };
}
